// ============================================
// FILE: /frontend/src/components/dashboard/MyListingsManager.jsx
// UPDATED: Mobile-first compact cards with modal details view
// ============================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Eye, MessageSquare, Star, ChevronRight } from 'lucide-react';
import { dashboardAPI } from '../../api/dashboard.api';
import toast from 'react-hot-toast';
import PromoteListingModal from './PromoteListingModal';
import ListingDetailsModal from './ListingDetailsModal';

export default function MyListingsManager({ listings, onUpdate }) {
  console.log('🎨 [MyListingsManager] Rendering with listings:', listings);

  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(null);
  const API_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:5000';
  
  // Promote modal state
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [selectedListingForPromote, setSelectedListingForPromote] = useState(null);
  
  // Details modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedListingForDetails, setSelectedListingForDetails] = useState(null);

  const filteredListings = listings.filter(listing => {
    if (filter === 'all') return true;
    return listing.status === filter.toUpperCase();
  });

  // Handle viewing listing details
  const handleViewDetails = (listing) => {
    console.log('[MyListingsManager] View details:', listing.id);
    setSelectedListingForDetails(listing);
    setDetailsModalOpen(true);
  };

  // Handle Edit
  const handleEdit = (listingId) => {
    console.log('[MyListingsManager] Edit listing:', listingId);
    navigate(`/listings/edit/${listingId}`);
  };

  // Handle Promote
  const handlePromoteClick = (listing) => {
    console.log('[MyListingsManager] Promote listing:', listing.id);
    setSelectedListingForPromote(listing);
    setPromoteModalOpen(true);
  };

  // Handle Delete
  const handleDelete = async (listingId, title) => {
    setLoading(listingId);
    try {
      await dashboardAPI.deleteListing(listingId);
      toast.success('Listing deleted successfully');
      onUpdate();
    } catch (error) {
      console.error('❌ [MyListingsManager] Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete');
    } finally {
      setLoading(null);
    }
  };

  // Handle Suspend
  const handleSuspend = async (listingId) => {
    setLoading(listingId);
    try {
      await dashboardAPI.suspendListing(listingId);
      toast.success('Listing suspended');
      onUpdate();
    } catch (error) {
      console.error('❌ [MyListingsManager] Suspend error:', error);
      toast.error('Failed to suspend');
    } finally {
      setLoading(null);
    }
  };

 // Handle Reactivate
const handleReactivate = async (listingId) => {
  setLoading(listingId);
  try {
    await dashboardAPI.reactivateListing(listingId);
    toast.success('Listing reactivated');
    onUpdate();
  } catch (error) {
    console.error('❌ [MyListingsManager] Reactivate error:', error);
    
    // Check if subscription expired
    if (error.response?.data?.expired) {
      // Store the listing ID for reactivation after payment
      sessionStorage.setItem('reactivatingListingId', listingId);
      
      toast.error('Subscription expired. Redirecting to checkout...');
      setTimeout(() => {
        navigate('/listings/checkout');  // ✅ Changed from /listings/payment
      }, 1500);  // ✅ Changed from 4500
    } 
    // Check if quota full
    else if (error.response?.data?.quotaFull) {
      toast.error('Ad limit reached. Upgrade or delete another ad.');
    } 
    // Generic error
    else {
      toast.error('Failed to reactivate');
    }
  } finally {
    setLoading(null);
  }
};

  const getStatusBadge = (status) => {
    const badges = {
      APPROVED: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      SUSPENDED: 'bg-gray-100 text-gray-800',
      REJECTED: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.PENDING;
  };

  // Get image URL helper
    const getImageUrl = (listing) => {
      let imageUrl = null;
      
      // imageUrl is an array, get the first item
      if (listing.imageUrl && listing.imageUrl.length > 0) {
        imageUrl = listing.imageUrl[0];
      } 
      // Fallback to photos array
      else if (listing.photos && listing.photos.length > 0) {
        imageUrl = listing.photos[0];
      }
      
      // Check if we have a valid string URL
      if (imageUrl && typeof imageUrl === 'string') {
        return imageUrl.startsWith('http')
          ? imageUrl 
          : `${API_BASE_URL}${imageUrl}`;
      }
      
      return '/placeholder-car.jpg';
    };

  return (
    <>
      <Card>
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="text-base sm:text-lg md:text-xl">My Listings</CardTitle>
          
          {/* Filter Buttons - 2x2 grid layout */}
          <div className="grid grid-cols-2 gap-2 mt-3 sm:mt-4">
            {['all', 'approved', 'pending', 'suspended'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-md text-xs sm:text-sm transition font-medium ${
                  filter === f 
                    ? 'bg-primary text-white' 
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
          <div className="space-y-3 sm:space-y-4">
            {filteredListings.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 sm:py-8 text-xs sm:text-sm">
                No listings found
              </p>
            ) : (
              filteredListings.map(listing => (
                <div 
                  key={listing.id} 
                  onClick={() => handleViewDetails(listing)}
                  className="border rounded-lg p-3 sm:p-4 hover:bg-muted/50 transition cursor-pointer group"
                >
                  <div className="flex gap-3 sm:gap-4">
                    
                    {/* Thumbnail - responsive size */}
                    <div className="relative flex-shrink-0">
                      <img
                        src={getImageUrl(listing)}
                        alt={listing.title}
                        className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md"
                        onError={(e) => {
                          e.target.src = '/placeholder-car.jpg';
                        }}
                      />
                      {listing.isFeatured && (
                        <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full p-1">
                          <Star className="w-3 h-3 text-white fill-current" />
                        </div>
                      )}
                    </div>

                    {/* Content - responsive text */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1 sm:mb-2">
                        <h3 className="font-semibold text-sm sm:text-base line-clamp-2 flex-1">
                          {listing.title}
                        </h3>
                        <span className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium flex-shrink-0 ${getStatusBadge(listing.status)}`}>
                          {listing.status}
                        </span>
                      </div>

                      <p className="text-sm sm:text-base md:text-lg font-bold text-primary mb-2 sm:mb-3">
                        KES {listing.price?.toLocaleString() || '0'}
                      </p>

                      {/* Stats - responsive layout */}
                      <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          {listing.viewsCount || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          {listing.inquiriesCount || 0}
                        </span>
                        <span className="ml-auto flex items-center gap-1 text-primary group-hover:gap-2 transition-all">
                          <span className="hidden sm:inline">View Details</span>
                          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Promote Modal */}
      {promoteModalOpen && selectedListingForPromote && (
        <PromoteListingModal
          isOpen={promoteModalOpen}
          onClose={() => {
            setPromoteModalOpen(false);
            setSelectedListingForPromote(null);
          }}
          listing={selectedListingForPromote}
          onSuccess={() => {
            onUpdate();
            setPromoteModalOpen(false);
            setSelectedListingForPromote(null);
          }}
        />
      )}

      {/* Details Modal */}
      {detailsModalOpen && selectedListingForDetails && (
        <ListingDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedListingForDetails(null);
          }}
          listing={selectedListingForDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onSuspend={handleSuspend}
          onReactivate={handleReactivate}
          onPromote={handlePromoteClick}
          loading={loading}
          getStatusBadge={getStatusBadge}
        />
      )}
    </>
  );
}